import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import { Layout, Spin } from "antd";
import Nps from "components/newNPS";
import TitlePage from "components/title-page";
import { bindActionCreators, Dispatch } from "redux";
import { Creators as LobbyActions } from "store/lobby/lobby-duck";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { ILocationsList, IUserData, MenuItemActive } from "store/user-session/user-session-model";
import { checkPermissionPage } from "utils/functions";
import SideBar from "./SideBar";
import * as S from "./styled/index.styled";
import TopBar from "./TopBar";

interface IPropsLayout {
  isLoading?: boolean;
  userData?: IUserData;
  isCollapseSidebar?: boolean;
  hasConfigurationLoaded?: boolean;
  hasAttendance?: boolean;
  hasRfidReading?: boolean;
  locationsList?: ILocationsList[];
  menuItemActive?: MenuItemActive;
}
interface IDispatchProps {
  userSessionUpdateUserData?: () => void;
  userSessionSaveInfoAsync?: () => void;
  userSessionCollapseSidebar?: () => void;
  userSessionUncollapseSidebar?: () => void;
  lobbyFetchHasConfigurationAsync?: () => void;
  userSessionLocationsAsync?: (...args) => void;
  userSessionUpdateLocationAsync?: (...args) => void;
  userSessionMenuItemActive?: (...args) => void;
  lobbyHasConfigurationNotLoaded?: () => void;
}

interface ComponentProps {
  title?: string;
  children: React.ReactNode;
  role: string;
}

type IProps = IPropsLayout & IDispatchProps & ComponentProps;

const LayoutPage: React.FC<IProps> = (props) => {
  const {
    children,
    title,
    isLoading,
    userData,
    role,
    isCollapseSidebar,
    hasAttendance,
    hasRfidReading,
    hasConfigurationLoaded,
    locationsList,
    menuItemActive,
    userSessionUpdateUserData,
    userSessionCollapseSidebar,
    userSessionUncollapseSidebar,
    lobbyFetchHasConfigurationAsync,
    lobbyHasConfigurationNotLoaded,
    userSessionMenuItemActive,
    userSessionLocationsAsync,
    userSessionUpdateLocationAsync,
  } = props;
  const router = useLocation();
  const navigation = useNavigate();
  const hasAnsweredNps = localStorage.getItem("hasAnsweredNps");

  useEffect(() => {
    datadogRum.setUser({
      email: userData?.email || "",
    });
  }, []);

  useEffect(() => {
    if (!userData?.locationId || userData.locationId <= 0) {
      return;
    }

    lobbyFetchHasConfigurationAsync();
  }, [userData?.locationId, lobbyFetchHasConfigurationAsync]);

  useEffect(() => {
    userSessionLocationsAsync();
  }, [userSessionLocationsAsync]);

  useEffect(() => {
    checkPermissionPage(router, navigation, hasAttendance, hasConfigurationLoaded, lobbyHasConfigurationNotLoaded, hasRfidReading);
  }, [hasAttendance, hasConfigurationLoaded, hasRfidReading]);

  if (!role) {
    return (
      <div style={S.LoadingContainer}>
        <Spin />
      </div>
    );
  }

  return (
    <Layout>
      {!hasAnsweredNps && <Nps />}
      <TitlePage title={title} />
      <TopBar
        username={userData?.username || ""}
        locationId={userData?.locationId || ""}
        locationDescription={userData?.location || ""}
        userSessionUpdateUserData={userSessionUpdateUserData}
        userSessionUpdateLocationAsync={userSessionUpdateLocationAsync}
        locationsList={locationsList}
      />
      <SideBar
        isLoading={isLoading}
        role={role}
        isCollapseSidebar={isCollapseSidebar}
        userSessionCollapseSidebar={userSessionCollapseSidebar}
        userSessionUncollapseSidebar={userSessionUncollapseSidebar}
        userSessionMenuItemActive={userSessionMenuItemActive}
        hasAttendance={hasAttendance}
        hasRfidReading={hasRfidReading}
        menuItemActive={menuItemActive}
      >
        {children}
      </SideBar>
    </Layout>
  );
};

const mapDispatchToActions = (dispatch: Dispatch) => bindActionCreators({ ...UserSessionActions, ...LobbyActions }, dispatch);

const mapStateToProps = (state, componentProps: ComponentProps) => ({
  userData: state.UserSession.userData,
  isLoading: state.UserSession.isLoading,
  isCollapseSidebar: state.UserSession.isCollapseSidebar,
  title: componentProps.title,
  children: componentProps.children,
  isConcierge: state.UserSession.isConcierge,
  role: componentProps.role,
  hasAttendance: state.Lobby.hasAttendance,
  hasConfigurationLoaded: state.Lobby.hasConfigurationLoaded,
  hasRfidReading: state.Lobby.hasRfidReading,
  locationsList: state.UserSession.locationsList,
  menuItemActive: state.UserSession.menuItemActive,
});

export default connect(mapStateToProps, mapDispatchToActions)(LayoutPage);
